// src/CalendarioView.js
// Calendário das pendências:
//  - pendências em "Atualizar" aparecem no dia e hora marcados para a atualização
//  - as demais pendências abertas aparecem no dia em que foram lançadas
//  - opcionalmente, as concluídas aparecem no dia da conclusão
import React, { useMemo, useState } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, startOfMonth, endOfMonth, addDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useColecao } from './dados';
import { BuscaSelect } from './ui';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: (d) => startOfWeek(d, { weekStartsOn: 0 }),
    getDay,
    locales: { 'pt-BR': ptBR },
});

const MENSAGENS = {
    today: 'Hoje', previous: 'Anterior', next: 'Próximo',
    month: 'Mês', week: 'Semana', day: 'Dia', agenda: 'Agenda',
    date: 'Data', time: 'Hora', event: 'Pendência', allDay: 'Dia todo',
    noEventsInRange: 'Nenhuma pendência neste período.',
    showMore: (n) => `+ ${n} mais`,
};

const CORES = {
    'Pendente': '#ef4444',
    'Ag. Retorno': '#fb923c',
    'Em Atendimento': '#3b82f6',
    'Ag. Pendencia': '#eab308',
    'Atualizar': '#a855f7',
    'Concluído': '#22c55e',
};

// "dd/mm/aaaa" + "hh:mm" -> Date
const textoParaData = (dt, hora) => {
    const m = String(dt || '').match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (!m) return null;
    const [h, mi] = String(hora || '00:00').split(':').map(Number);
    return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]), h || 0, mi || 0);
};

const dataAbertura = (t) =>
    (t.timestampCriacao && typeof t.timestampCriacao.toDate === 'function') ? t.timestampCriacao.toDate() : textoParaData(t.dtLancamento, t.hora);

const dataAtualizacao = (t) =>
    (t.timestampAtualizacao && typeof t.timestampAtualizacao.toDate === 'function') ? t.timestampAtualizacao.toDate() : textoParaData(t.dtAtualizacao, t.horaAtualizacao);

const CalendarioView = ({ tasks, consultores, onOpenTask }) => {
    const [tecnico, setTecnico] = useState('');
    const [verAberturas, setVerAberturas] = useState(true);
    const [verConcluidas, setVerConcluidas] = useState(false);
    const [view, setView] = useState('month');
    const [data, setData] = useState(new Date());

    // período visível (com margem para as semanas que aparecem no mês)
    const periodo = useMemo(() => ({
        inicio: addDays(startOfMonth(data), -7),
        fim: addDays(endOfMonth(data), 7),
    }), [data]);

    // concluídas (lista compartilhada com as outras telas), só as do período visível
    const { dados: todasConcluidas } = useColecao('concluidas', verConcluidas);
    const concluidas = useMemo(() => {
        if (!verConcluidas) return [];
        return todasConcluidas.filter((t) => {
            const d = t.timestampconclusao && t.timestampconclusao.toDate ? t.timestampconclusao.toDate() : null;
            return d && d >= periodo.inicio && d <= periodo.fim;
        });
    }, [verConcluidas, todasConcluidas, periodo]);

    const eventos = useMemo(() => {
        const lista = [];
        const doTecnico = (t) => !tecnico || t.tecnico === tecnico;
        const nome = (t) => `${t.empresa || ''}${t.nrPendenciaSequencial ? ` · #${t.nrPendenciaSequencial}` : ''}`;

        tasks.filter(doTecnico).forEach((t) => {
            if (t.status === 'Atualizar') {
                const d = dataAtualizacao(t);
                if (d) {
                    lista.push({ title: `Atualizar: ${nome(t)}`, start: d, end: new Date(d.getTime() + 60 * 60000), task: t, cor: CORES.Atualizar });
                    return;
                }
            }
            if (!verAberturas) return;
            const d = dataAbertura(t);
            if (d) lista.push({ title: nome(t), start: d, end: d, allDay: true, task: t, cor: CORES[t.status] || '#6b7280' });
        });

        concluidas.filter(doTecnico).forEach((t) => {
            const d = t.timestampconclusao && t.timestampconclusao.toDate ? t.timestampconclusao.toDate() : null;
            if (d) lista.push({ title: `✓ ${nome(t)}`, start: d, end: d, allDay: true, task: t, cor: CORES['Concluído'] });
        });
        return lista;
    }, [tasks, concluidas, tecnico, verAberturas]);

    const qtdAtualizar = eventos.filter((e) => e.task.status === 'Atualizar' && !e.allDay).length;

    return (
        <div className="bg-white rounded-lg shadow p-4 sm:p-5">
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <h2 className="text-xl font-semibold text-gray-800 mr-auto">Calendário de Pendências</h2>
                <label className="flex items-center gap-1.5 text-sm text-gray-700">
                    <input type="checkbox" checked={verAberturas} onChange={(e) => setVerAberturas(e.target.checked)} />
                    Abertas (dia do lançamento)
                </label>
                <label className="flex items-center gap-1.5 text-sm text-gray-700">
                    <input type="checkbox" checked={verConcluidas} onChange={(e) => setVerConcluidas(e.target.checked)} />
                    Concluídas
                </label>
                <BuscaSelect className="w-56" placeholder="Todos os técnicos" vazio="Nenhum técnico encontrado"
                    valor={tecnico} onChange={setTecnico}
                    opcoes={consultores.map((c) => ({ valor: c.nome, rotulo: c.nome || '', detalhe: c.ativo === false ? 'inativo' : '' }))} />
            </div>

            {/* legenda */}
            <div className="flex flex-wrap gap-3 mb-3 text-xs text-gray-600">
                {Object.entries(CORES).map(([s, c]) => (
                    <span key={s} className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{ background: c }} />{s}</span>
                ))}
                <span className="ml-auto">{qtdAtualizar} atualização(ões) agendada(s) no calendário</span>
            </div>

            <div style={{ height: 'calc(100vh - 260px)', minHeight: 520 }}>
                <Calendar
                    localizer={localizer}
                    culture="pt-BR"
                    events={eventos}
                    view={view}
                    onView={setView}
                    date={data}
                    onNavigate={setData}
                    views={['month', 'week', 'day', 'agenda']}
                    messages={MENSAGENS}
                    popup
                    onSelectEvent={(ev) => onOpenTask(ev.task)}
                    tooltipAccessor={(ev) => `${ev.task.empresa} · ${ev.task.modulo || ''} · ${ev.task.usuario || ''} · Técnico: ${ev.task.tecnico || ''}\n${ev.task.descricao || ''}`}
                    eventPropGetter={(ev) => ({
                        style: {
                            backgroundColor: ev.cor,
                            borderColor: ev.cor,
                            color: ev.cor === CORES['Ag. Pendencia'] ? '#111' : '#fff',
                            fontSize: 12,
                        },
                    })}
                />
            </div>
        </div>
    );
};

export default CalendarioView;
